import React, { useState, useEffect, useRef, useMemo } from "react";
import { IoPersonCircle } from "react-icons/io5";
import { FaFile, FaFileArchive, FaFileAudio, FaFileExcel, FaFileImage, FaFilePdf, FaFileVideo, FaFileWord, FaRegTrashAlt } from "react-icons/fa";
import { FaUsers } from "react-icons/fa";
import { LuForward } from "react-icons/lu";
import { BsPaperclip, BsThreeDotsVertical } from "react-icons/bs";
import useDrivePicker from "react-google-drive-picker";
import { LiaGoogleDrive } from "react-icons/lia";
import toast from "react-hot-toast";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { DataState } from "../../context/DataProvider";
import { forwardGmailEmail, forwardOutlookEmail } from "../../services/api";
import { FiDownload } from "react-icons/fi";
import SunEditorComponent from "../chat/SunEditorComponent";

const getFileIcon = (mimeType) => {
    if (mimeType?.includes("pdf")) return <FaFilePdf className="text-red-500" size={20} />;
    if (mimeType?.includes("word") || mimeType?.includes("officedocument.wordprocessingml")) return <FaFileWord className="text-[#5652CF]" size={20} />;
    if (mimeType?.includes("excel") || mimeType?.includes("officedocument.spreadsheetml")) return <FaFileExcel className="text-green-500" size={20} />;
    if (mimeType?.includes("image")) return <FaFileImage className="text-yellow-500" size={20} />;
    if (mimeType?.includes("zip") || mimeType?.includes("rar") || mimeType?.includes("archive")) return <FaFileArchive className="text-gray-500" size={20} />;
    if (mimeType?.includes("audio")) return <FaFileAudio className="text-purple-500" size={20} />;
    if (mimeType?.includes("video")) return <FaFileVideo className="text-indigo-500" size={20} />;
    return <FaFile className="text-gray-500" size={20} />;
};

const AttachmentItem = ({ file, onRemove }) => {
    const [hovered, setHovered] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const handleDownloadClick = async () => {
        if (isDownloading || file.from !== "local") return;
        setIsDownloading(true);

        try {
            const url = URL.createObjectURL(file.file);
            const a = document.createElement("a");
            a.href = url;
            a.download = file.file.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download failed:", error);
            toast.error("Download failed. Please try again.");
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div
            className="inline-flex items-center justify-center w-40 h-10 p-2 py-3 text-gray-800 border border-gray-300 rounded-lg shadow-md cursor-pointer"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {hovered && file.from === "local" ? (
                <div className="flex items-center justify-center w-full gap-4">
                    {isDownloading ? (
                        <AiOutlineLoading3Quarters className="text-gray-600 animate-spin" size={16} />
                    ) : (
                        <FiDownload
                            onClick={handleDownloadClick}
                            className="text-gray-600 cursor-pointer hover:text-black"
                            size={16}
                        />
                    )}
                    <button onClick={() => onRemove(file)} className="ml-2 text-gray-600 hover:text-red-400">&times;</button>
                </div>
            ) : (
                <div className="flex items-center w-full space-x-2">
                    <div className="flex items-center justify-center w-8 h-8">
                        {getFileIcon(file?.file.mimeType || file?.file.type)}
                    </div>
                    <span className="text-sm font-medium truncate">
                        {file?.file.filename || file?.file.name}
                    </span>
                    {hovered && (
                        <button onClick={() => onRemove(file)} className="ml-auto text-gray-600 hover:text-red-400">&times;</button>
                    )}
                </div>
            )}
        </div>
    );
};

const ForwardEmail = ({
    message_id,
    setShowForwardPanel,
    message,
    setMessage,
    recepientsEmails = [],
    setRecepientsEmails,
    inputValue = "",
    setInputValue,
    isOutlook,

}) => {
    const fileInputRef = useRef(null);
    const { userInfo, userGoogleId } = DataState();
    const [loading, setLoading] = useState(false);
    const [ccs, setCcs] = useState([]);
    const [bccs, setBccs] = useState([]);
    const [inputCcValue, setInputCcValue] = useState("");
    const [inputBccValue, setInputBccValue] = useState("");
    const [showCc, setShowCc] = useState(false);
    const [showBcc, setShowBcc] = useState(false);

    const [localAttachments, setLocalAttachments] = useState([]);
    const [driveAttachments, setDriveAttachments] = useState([]);

    // New state to handle just updated content from AI or other sources
    const [justUpdatedValue, setJustUpdatedValue] = useState("");

    // Corrected the use of the useDrivePicker hook
    const [openPicker] = useDrivePicker();

    const allAttachments = useMemo(() => {
        const local = localAttachments.map(file => ({ name: file.name, from: "local", file }));
        const drive = driveAttachments.map(file => ({ name: file.name, from: "drive", id: file.id, file }));
        return [...local, ...drive];
    }, [localAttachments, driveAttachments]);

    // Use useEffect to update the message state whenever originalEmailBody changes.
    // This ensures the editor is pre-populated and updates correctly.
useEffect(() => {
    if (message) {
        setMessage(message);
        setJustUpdatedValue(message);
    }
    // run only once when mounting
     // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);


    const handleKeyDown = (event, type) => {
        const value = (type === "to" ? inputValue : type === "cc" ? inputCcValue : inputBccValue).trim().replace(/,$/, "");
        if ((event.key === "Enter" || event.key === "," || event.key === " ") && value) {
            event.preventDefault();
            const emailPattern = /^[a-zA-Z0-9._%+-]+@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;
            const match = value.match(emailPattern);

            if (match) {
                if (type === "to") {
                    if (!recepientsEmails.includes(value)) setRecepientsEmails(prev => [...prev, value]);
                    setInputValue("");
                } else if (type === "cc") {
                    if (!ccs.includes(value)) setCcs(prev => [...prev, value]);
                    setInputCcValue("");
                } else if (type === "bcc") {
                    if (!bccs.includes(value)) setBccs(prev => [...prev, value]);
                    setInputBccValue("");
                }
            } else {
                toast.error("Invalid email format!");
            }
        }
    };

    const removeEmail = (emailToRemove, type) => {
        if (type === "to") {
            setRecepientsEmails(prev => prev.filter((email) => email !== emailToRemove));
        } else if (type === "cc") {
            setCcs(prev => prev.filter((email) => email !== emailToRemove));
        } else if (type === "bcc") {
            setBccs(prev => prev.filter((email) => email !== emailToRemove));
        }
    };

    const handleForward = async () => {
        setLoading(true);

        if (!recepientsEmails.length) {
            toast.error("Please enter at least one recipient.");
            setLoading(false);
            return;
        }
        console.log("Message to forward:", message);

        const forwardData = {
            recipients: recepientsEmails,
            ccs: ccs,
            bccs: bccs,
            message_id: message_id,
            comment: message,
        };

        try {
            if (isOutlook) {
                await forwardOutlookEmail(userGoogleId, forwardData);
            } else {
                await forwardGmailEmail(userGoogleId, forwardData, isOutlook);
            }
            toast.success("Email forwarded successfully!");
            setRecepientsEmails([]);
            setInputValue("");
            setMessage("");
            setJustUpdatedValue("clearing")
            setJustUpdatedValue("");
            console.log("Updated value cleared:", justUpdatedValue);
            
        } catch (error) {
            toast.error("Failed to forward email.");
            console.error("Forward email error:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleTrashClick = () => {
        setMessage("");
        setJustUpdatedValue("clearing");
        setJustUpdatedValue("");
        setShowForwardPanel(false);
        setRecepientsEmails([]);
        setLocalAttachments([]);
        setDriveAttachments([]);
    };

    const handlePaperClipClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        setLocalAttachments((prevAttachments) => [...prevAttachments, ...files]);
        event.target.value = null;
    };

    const handleRemove = (fileToRemove) => {
        if (fileToRemove.from === "local") {
            setLocalAttachments((prev) => prev.filter((file) => file.name !== fileToRemove.name));
        } else if (fileToRemove.from === "drive") {
            setDriveAttachments((prev) => prev.filter((file) => file.id !== fileToRemove.id));
        }
    };

    const handleOpenPicker = () => {
        openPicker({
            clientId: "YOUR_GOOGLE_CLIENT_ID",
            developerKey: "YOUR_GOOGLE_API_KEY",
            viewId: "DOCS",
            showUploadView: true,
            showUploadFolders: true,
            supportDrives: true,
            multiselect: true,
            callbackFunction: (data) => {
                if (data.action === "picked") {
                    const files = data.docs.map((file) => ({
                        id: file.id,
                        name: file.name,
                        mimeType: file?.mimeType || file?.type,
                        url: file.url,
                    }));
                    setDriveAttachments((prev) => [...prev, ...files]);
                }
            },
        });
    };

    return (
        <div className="flex flex-row w-full transition duration-200">
            <div className="px-4 pl-0">
                {userInfo?.picture ? (
                    <img
                        src={userInfo.picture}
                        alt="User Profile"
                        className="object-cover w-10 h-10 rounded-full"
                    />
                ) : (
                    <IoPersonCircle className="text-gray-700" size={40} />
                )}
            </div>
            <div className="flex flex-col w-full">
                <div className="w-full p-4 bg-white border border-gray-300 shadow-md rounded-xl">
                    <div className="flex flex-row items-start w-full gap-2 mb-3 text-gray-800">
                        <FaUsers className="self-center hidden w-5 h-5 mt-2 text-gray-600 sm:block" />
                        <div className="flex flex-wrap items-center flex-grow w-full gap-1 p-2 text-sm border border-gray-300 rounded-lg min-h-[40px]">
                            {recepientsEmails.map((email, index) => (
                                <span
                                    key={index}
                                    className="inline-flex items-center px-2 py-1 text-gray-800 bg-gray-200 rounded-lg"
                                >
                                    {email}
                                    <button
                                        onClick={() => removeEmail(email, "to")}
                                        className="ml-2 text-gray-600 hover:text-red-400"
                                    >
                                        &times;
                                    </button>
                                </span>
                            ))}
                            <input
                                type="text"
                                className="flex-grow min-w-[120px] bg-transparent outline-none text-gray-800"
                                placeholder="Enter recipients..."
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={(e) => handleKeyDown(e, "to")}
                            />
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setShowCc(!showCc)}
                                className={`flex items-center justify-center text-sm border border-gray-300 py-1 px-2 rounded-lg min-h-[40px] hover:bg-gray-200 transition-colors duration-200 ${showCc ? "bg-gray-200" : "bg-white"} w-full sm:w-auto text-center`}
                            >
                                Cc
                            </button>
                            <button
                                onClick={() => setShowBcc(!showBcc)}
                                className={`flex items-center justify-center text-sm border border-gray-300 py-1 px-2 rounded-lg min-h-[40px] hover:bg-gray-200 transition-colors duration-200 ${showBcc ? "bg-gray-200" : "bg-white"} w-full sm:w-auto text-center`}
                            >
                                Bcc
                            </button>
                        </div>
                    </div>

                    {showCc && (
                        <div className="flex flex-wrap items-center gap-1 text-sm border border-gray-300 p-2 rounded-lg w-full min-h-[40px] mb-3">
                            {ccs.map((email, index) => (
                                <span
                                    key={index}
                                    className="inline-flex items-center px-2 py-1 text-gray-800 bg-gray-200 rounded-lg"
                                >
                                    {email}
                                    <button
                                        onClick={() => removeEmail(email, "cc")}
                                        className="ml-2 text-gray-600 hover:text-red-400"
                                    >
                                        &times;
                                    </button>
                                </span>
                            ))}
                            <input
                                type="text"
                                className="bg-transparent outline-none text-gray-800 flex-grow min-w-[120px]"
                                placeholder="Cc..."
                                value={inputCcValue}
                                onChange={(e) => setInputCcValue(e.target.value)}
                                onKeyDown={(e) => handleKeyDown(e, "cc")}
                            />
                        </div>
                    )}

                    {showBcc && (
                        <div className="flex flex-wrap items-center gap-1 text-sm border border-gray-300 p-2 rounded-lg w-full min-h-[40px] mb-3">
                            {bccs.map((email, index) => (
                                <span
                                    key={index}
                                    className="inline-flex items-center px-2 py-1 text-gray-800 bg-gray-200 rounded-lg"
                                >
                                    {email}
                                    <button
                                        onClick={() => removeEmail(email, "bcc")}
                                        className="ml-2 text-gray-600 hover:text-red-400"
                                    >
                                        &times;
                                    </button>
                                </span>
                            ))}
                            <input
                                type="text"
                                className="bg-transparent outline-none text-gray-800 flex-grow min-w-[120px]"
                                placeholder="Bcc..."
                                value={inputBccValue}
                                onChange={(e) => setInputBccValue(e.target.value)}
                                onKeyDown={(e) => handleKeyDown(e, "bcc")}
                            />
                        </div>
                    )}

                    <SunEditorComponent
                        value={message}
                        onChange={setMessage}
                        justUpdatedValue={justUpdatedValue}
                    />

                    {allAttachments.length > 0 && (
                        <div className="flex flex-wrap items-center flex-grow gap-2 p-1 mt-2">
                            {allAttachments.map((file, index) => (
                                <AttachmentItem key={index} file={file} onRemove={handleRemove} />
                            ))}
                        </div>
                    )}

                    <div className="flex flex-col items-center justify-between mt-3 text-gray-800">
                        <div className="flex items-center justify-between w-full gap-2 p-2 pl-0 pr-0 space-x-3 sm:gap-0">
                            <div className="flex items-center space-x-3">
                                <button
                                    onClick={handleForward}
                                    disabled={loading || recepientsEmails.length === 0}
                                    className="flex items-center justify-between px-2 font-medium text-white transition-colors bg-[#5652CF] rounded-full hover:bg-gray-600"
                                >
                                    <div className="flex items-center justify-center h-full pl-1">
                                        <LuForward size={20} />
                                    </div>
                                    <div className="py-1 pl-2 pr-4 ">
                                        {loading ? "Forwarding..." : "Forward"}
                                    </div>
                                </button>
                                <div className="flex">
                                    <div
                                        onClick={handlePaperClipClick}
                                        className="flex items-center justify-center w-8 h-8 text-gray-600 transition-colors rounded-md cursor-pointer hover:text-gray-900 hover:bg-gray-200"
                                    >
                                        <BsPaperclip size={18} />
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            className="hidden"
                                            onChange={handleFileChange}
                                            multiple
                                        />
                                    </div>
                                    <div
                                        onClick={handleOpenPicker}
                                        className="flex items-center justify-center w-8 h-8 text-gray-600 transition-colors rounded-md cursor-pointer hover:text-gray-900 hover:bg-gray-200"
                                    >
                                        <LiaGoogleDrive size={18} />
                                    </div>
                                    <div className="flex items-center justify-center w-8 h-8 text-gray-600 transition-colors rounded-md cursor-pointer hover:text-gray-900 hover:bg-gray-200">
                                        <BsThreeDotsVertical size={18} />
                                    </div>
                                </div>
                            </div>
                            <div className="w-auto ml-auto">
                                <button
                                    onClick={handleTrashClick}
                                    className="text-gray-600 transition-colors hover:text-gray-900"
                                >
                                    <FaRegTrashAlt size={18} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForwardEmail;